import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { documents, type DocumentSettings } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { useToast } from '@/shared/ui/useToast';

import { formatBytes } from './envio';

/** Um gigabyte, na conta do OnPoint (LimitDiskSpaceGB). */
const GB = 1024 * 1024 * 1024;

/**
 * Aba "Configuracao" do repositorio: guarda do arquivo excluido e cota.
 *
 * A guarda e decisao do dono ainda aberta, com o comportamento do OnPoint como
 * padrao (docs/ADR/0028-documentos-donos-e-painel.md): a exclusao guarda o arquivo e
 * permite restaurar. A cota de disco e por empresa, com a opcao de passar dela, e
 * quem a define e a administracao da plataforma (ADR 0072): aqui ela so aparece.
 */
export function DocumentSettingsTab() {
  const configuracao = useQuery({
    queryKey: ['document-settings'],
    queryFn: ({ signal }) => documents.settings(signal),
  });
  const armazenamento = useQuery({
    queryKey: ['document-storage'],
    queryFn: ({ signal }) => documents.storage(signal),
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Uso do armazenamento"
          description="Documentos, arquivos guardados de excluidos, modelos em Word e documentos gerados."
        />
        <CardBody>
          {armazenamento.data ? (
            <p className="text-sm text-ink-700">
              Em uso: {formatBytes(armazenamento.data.used_bytes ?? 0)}
              {armazenamento.data.quota_bytes
                ? ` de ${formatBytes(armazenamento.data.quota_bytes)}`
                : ', sem cota'}
              .
              {armazenamento.data.over_quota ? (
                <span className="ml-1 font-medium text-danger-700">Acima da cota.</span>
              ) : null}
            </p>
          ) : (
            <p className="text-sm text-ink-500">Carregando o uso.</p>
          )}
        </CardBody>
      </Card>

      {configuracao.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {configuracao.error.message}
        </Alert>
      ) : configuracao.data ? (
        <SettingsForm
          key={configuracao.data.updated_at ?? 'padrao'}
          atual={configuracao.data}
        />
      ) : null}
    </div>
  );
}

function SettingsForm({ atual }: { atual: DocumentSettings }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeConfigurar = can('documentos.configuracao.update');

  const [guardar, setGuardar] = useState(atual.retain_deleted_files);

  const salvar = useMutation({
    mutationFn: () => documents.updateSettings({ retain_deleted_files: guardar }),
    onSuccess: () => {
      toast.success('Configuracao gravada.');
      void queryClient.invalidateQueries({ queryKey: ['document-settings'] });
      void queryClient.invalidateQueries({ queryKey: ['document-storage'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card>
      <CardHeader
        title="Configuracao do repositorio"
        description={
          atual.updated_at
            ? `Alterada por ${atual.updated_label || 'quem configura'} em ${formatDateTime(atual.updated_at)}.`
            : 'Ainda no padrao: guarda o arquivo excluido.'
        }
      />
      <CardBody className="flex flex-col gap-4">
        <label className="flex items-start gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="mt-0.5 size-4"
            checked={guardar}
            disabled={!podeConfigurar}
            onChange={(event) => setGuardar(event.target.checked)}
          />
          <span>
            Guardar o arquivo do documento excluido
            <span className="block text-2xs text-ink-500">
              Ligado, o excluido pode ser baixado e restaurado. Desligado, a exclusao
              apaga o arquivo e fica so o registro. Vale para as proximas exclusoes.
            </span>
          </span>
        </label>

        {/* A cota e da administracao da plataforma (P-PLAT): aqui so se le. */}
        <div className="rounded-md border border-line bg-surface-sunken px-3 py-2 text-sm text-ink-700">
          <p>
            Cota de disco:{' '}
            <span className="font-medium text-ink-900">{cotaEmTexto(atual)}</span>
          </p>
          <p className="mt-0.5 text-2xs text-ink-500">
            Definida pela administracao da plataforma. Sem permissao de passar da cota,
            o envio que passaria dela e recusado; com ela, o envio segue com aviso.
          </p>
        </div>

        {podeConfigurar ? (
          <div className="flex justify-end">
            <Button loading={salvar.isPending} onClick={() => salvar.mutate()}>
              Salvar configuracao
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** A cota em gigabytes, como o OnPoint (LimitDiskSpaceGB), e se pode ser ultrapassada. */
function cotaEmTexto(atual: DocumentSettings): string {
  const gb =
    atual.storage_quota_gb ??
    (atual.storage_quota_bytes ? Math.ceil(atual.storage_quota_bytes / GB) : null);
  if (!gb) return 'sem cota';
  return atual.storage_quota_soft
    ? `${gb} GB, pode passar da cota`
    : `${gb} GB, sem passar da cota`;
}
